import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Trophy, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import tournamentService from '../../services/tournamentService';
import { getFullImageUrl } from '../../utils/imageUpload';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const TournamentInfo = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const TournamentName = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const WarningText = styled.p`
  color: #ffd93d;
  font-size: 14px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormContainer = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const FormTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
`;

const SelectContainer = styled.div`
  margin-bottom: 20px;
`;

const SelectLabel = styled.label`
  display: block;
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
  
  option {
    background: #1a1a2e;
    color: #ffffff;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin: 20px 0;
  padding: 15px;
  background: linear-gradient(90deg, rgba(255, 107, 107, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: #b8b8d1;
`;

const FinishTournamentModal = ({ 
  show, 
  tournament, 
  onClose, 
  onFinish 
}) => {
  const [loading, setLoading] = useState(false);
  const [winnerTeamId, setWinnerTeamId] = useState('');
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState('');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show && tournament) {
      setWinnerTeamId('');
      setError('');
      fetchTeams();
    }
  }, [show, tournament]);

  const fetchTeams = async () => {
    if (!tournament?.id) return;
    
    try {
      setLoading(true);
      const response = await tournamentService.getTournament(tournament.id);
      setTeams(response.tournamentTeams || []);
    } catch (err) {
      setError('خطا در بارگذاری تیم‌ها');
      console.error('Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    if (!winnerTeamId) {
      setError('لطفاً تیم برنده را انتخاب کنید');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onFinish(tournament.id, winnerTeamId);
      toast.success('تورنومنت با موفقیت به پایان رسید');
      onClose();
    } catch (err) {
      setError(err.message || 'خطا در پایان دادن تورنومنت');
      console.error('Error finishing tournament:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!show || !tournament) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            پایان تورنومنت و انتخاب برنده
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <TournamentInfo>
            <TournamentName>تورنومنت: {tournament.name}</TournamentName>
            <WarningText>
              <AlertCircle size={16} />
              این عمل تورنومنت را به عنوان "تکمیل شده" علامت‌گذاری می‌کند و قابل بازگشت نیست
            </WarningText>
          </TournamentInfo>

          {loading ? (
            <LoadingSpinner>
              <div>در حال بارگذاری تیم‌ها...</div>
            </LoadingSpinner>
          ) : (
            <FormContainer>
              <FormTitle>انتخاب تیم برنده</FormTitle>
              
              <SelectContainer>
                <SelectLabel>تیم برنده</SelectLabel>
                <Select
                  value={winnerTeamId}
                  onChange={(e) => setWinnerTeamId(e.target.value)}
                  required
                >
                  <option value="">-- انتخاب تیم برنده --</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.teamName}
                    </option>
                  ))}
                </Select>
              </SelectContainer>

              {error && (
                <ErrorMessage>
                  <AlertCircle size={16} />
                  {error}
                </ErrorMessage>
              )}

              <ActionButtons>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onClose}
                  disabled={loading}
                  icon={<X size={16} />}
                >
                  انصراف
                </Button>
                <Button 
                  type="button" 
                  variant="primary" 
                  onClick={handleFinish}
                  disabled={loading || !winnerTeamId}
                  icon={<CheckCircle size={16} />}
                >
                  {loading ? 'در حال پایان...' : 'پایان و تعیین برنده'}
                </Button>
              </ActionButtons>
            </FormContainer>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default FinishTournamentModal;
